import React from 'react';
import {
  Alert,
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  IconButton,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ProgressPanel from './ProgressPanel';

const ResultsPanel = ({
  t,
  loading,
  targetUnitLabel,
  currentCount,
  fitResults,
  highResResults,
  highResSettings,
  baseTotals,
  deltas,
  fitLoading,
  fitError,
  fitStage,
  fitProgress,
  formatNumber,
  formatDecimal,
  formatCI,
  formatPValue,
  formatPercent,
  formatDeltaWithPercent,
}) => {
  const models = [
    {
      id: 'solowSmith',
      key: 'solow',
      label: t('estimationPage.modelSolowSmith'),
      description: t('estimationPage.explanationSolow'),
      base: fitResults?.solowSmith,
      highRes: highResResults?.solowSmith,
      color: '#4dd0e1',
    },
    {
      id: 'logistic',
      key: 'logistic',
      label: t('estimationPage.modelLogistic'),
      description: t('estimationPage.explanationLogistic'),
      base: fitResults?.logistic,
      highRes: highResResults?.logistic,
      color: '#8884d8',
    },
    {
      id: 'gompertz',
      key: 'gompertz',
      label: t('estimationPage.modelGompertz'),
      description: t('estimationPage.explanationGompertz'),
      base: fitResults?.gompertz,
      highRes: highResResults?.gompertz,
      color: '#ffc658',
    },
  ];

  const hasHighRes = highResResults && (highResResults.solowSmith || highResResults.logistic || highResResults.gompertz);

  const getBaseTotal = (m) => {
    if (m.id === 'solowSmith') return m.base?.total;
    if (m.id === 'logistic') return m.base?.params?.L;
    if (m.id === 'gompertz') return m.base?.params?.a;
    return null;
  };

  const renderCell = (content) => (
    <TableCell align="center" sx={{ color: 'text.secondary', fontSize: '0.95rem' }}>
      {content || '-'}
    </TableCell>
  );

  const renderLabel = (label, help) => (
    <TableCell component="th" scope="row" sx={{ fontWeight: 500, color: 'text.primary' }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {label}
        {help && (
          <Tooltip title={help} arrow placement="right">
            <IconButton size="small" sx={{ ml: 0.5, p: 0.5, color: 'text.disabled' }}>
              <InfoOutlinedIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </TableCell>
  );

  return (
    <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mb: 3, backdropFilter: 'blur(5px)', backgroundColor: 'rgba(30,30,30,0.5)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <AssessmentIcon sx={{ mr: 1 }} />
        <Typography variant="h6">{t('resultsTitle')}</Typography>
      </Box>

      {/* Current Count Banner */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            {t('estimationPage.currentCount')}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            {formatNumber(currentCount)}
          </Typography>
        </Box>
        {fitResults?.curveModelAverage && (
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('estimationPage.curveModelAverageTitle')} ({t('estimationPage.estimatedTotal')})
            </Typography>
            <Typography variant="h5" color="primary">
              {formatNumber(fitResults.curveModelAverage.estimate)}
            </Typography>
          </Box>
        )}
      </Paper>

      {fitLoading && (
        <ProgressPanel
          size={28}
          title={t('estimationPage.analysisInProgress')}
          stage={fitStage}
          progress={fitProgress}
          containerSx={{ py: 3 }}
        />
      )}

      {fitError && !fitLoading && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {fitError}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
        <Table sx={{ minWidth: 650 }} aria-label="model comparison table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ borderBottom: '2px solid rgba(255,255,255,0.1)' }} width="25%" />
              {models.map((m) => (
                <TableCell
                  key={m.id}
                  align="center"
                  sx={{
                    borderBottom: `2px solid ${m.color}`,
                    pb: 1,
                  }}
                  width="25%"
                >
                  <Typography variant="h6" sx={{ color: m.color, fontWeight: 'bold' }}>
                    {m.label}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Base Fit Section */}
            <TableRow>
              <TableCell colSpan={4} sx={{ py: 1, backgroundColor: 'rgba(255,255,255,0.02)' }}>
                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                  {t('estimationPage.baseAnalysisTitle') || 'Base Analysis'}
                </Typography>
              </TableCell>
            </TableRow>

            <TableRow hover>
              {renderLabel(t('estimationPage.estimatedTotal'), t('estimationPage.helpEstimatedTotal'))}
              {models.map((m) => {
                const val = getBaseTotal(m);
                return (
                  <TableCell key={m.id} align="center">
                    {val ? (
                      <Typography variant="h6" color="primary">
                        {formatNumber(val)}
                      </Typography>
                    ) : '-'}
                  </TableCell>
                );
              })}
            </TableRow>

            <TableRow hover>
              {renderLabel(t('estimationPage.undiscovered'), t('estimationPage.helpUndiscovered'))}
              {models.map((m) => {
                const val = getBaseTotal(m);
                return renderCell(val ? formatNumber(val - currentCount) : null);
              })}
            </TableRow>

            <TableRow hover>
              {renderLabel(t('estimationPage.profileInterval'), t('estimationPage.helpProfileInterval'))}
              {models.map((m) => renderCell(m.id === 'solowSmith' ? formatCI(m.base?.ci) : '-'))}
            </TableRow>

            <TableRow hover>
              {renderLabel(t('estimationPage.bootstrapInterval'), t('estimationPage.helpBootstrapInterval'))}
              {models.map((m) => {
                const ci = m.id === 'solowSmith' ? m.base?.bootstrapCI : m.base?.ci;
                return renderCell(formatCI(ci));
              })}
            </TableRow>

            <TableRow hover>
              {renderLabel(t('estimationPage.rSquared'), t('estimationPage.helpRSquared'))}
              {models.map((m) => renderCell(formatDecimal(m.base?.rSquared)))}
            </TableRow>

            <TableRow hover>
              {renderLabel(t('estimationPage.aiccLabel'), t('estimationPage.helpAICc'))}
              {models.map((m) => renderCell(formatNumber(m.base?.diagnostics?.aicc)))}
            </TableRow>

            <TableRow hover>
              {renderLabel(t('estimationPage.solowGofLabel'), t('estimationPage.helpSolowGof'))}
              {models.map((m) => renderCell(m.id === 'solowSmith' ? formatPValue(m.base?.diagnostics?.gofPValue) : '-'))}
            </TableRow>

            {/* High-Res Section */}
            {hasHighRes && (
              <>
                <TableRow>
                  <TableCell colSpan={4} sx={{ py: 1, mt: 2, backgroundColor: 'rgba(255,255,255,0.02)' }}>
                    <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                      {t('estimationPage.highResSummaryTitle')}
                    </Typography>
                  </TableCell>
                </TableRow>

                <TableRow hover>
                  {renderLabel(t('estimationPage.estimatedTotal'), t('estimationPage.helpEstimatedTotal'))}
                  {models.map((m) => (
                    <TableCell key={m.id} align="center">
                      {m.highRes?.total ? (
                        <Typography variant="h6" color="secondary">
                          {formatNumber(m.highRes.total)}
                        </Typography>
                      ) : '-'}
                    </TableCell>
                  ))}
                </TableRow>

                <TableRow hover>
                  {renderLabel(t('estimationPage.undiscovered'), t('estimationPage.helpUndiscovered'))}
                  {models.map((m) => renderCell(m.highRes?.undiscovered ? formatNumber(m.highRes.undiscovered) : null))}
                </TableRow>

                <TableRow hover>
                  {renderLabel(t('estimationPage.highResIntervalLabel'), t('estimationPage.highResIntervalHelp'))}
                  {models.map((m) => (
                    <TableCell key={m.id} align="center" sx={{ color: 'text.secondary' }}>
                      {m.highRes?.ci ? (
                        <>
                          {formatCI(m.highRes.ci)}
                          {m.highRes.ciComplete === false && (
                            <Typography variant="caption" display="block" color="error">
                              {t('estimationPage.highResCIPartialNote')}
                            </Typography>
                          )}
                        </>
                      ) : '-'}
                    </TableCell>
                  ))}
                </TableRow>

                <TableRow hover>
                  {renderLabel(t('estimationPage.highResDeltaLabel'), t('estimationPage.highResDeltaHelp'))}
                  {models.map((m) => renderCell(formatDeltaWithPercent(deltas[m.key], baseTotals[m.key])))}
                </TableRow>

                <TableRow hover>
                  {renderLabel(t('estimationPage.highResGroupsLabel'), t('estimationPage.highResGroupsHelp'))}
                  {models.map((m) => renderCell(
                    m.highRes ? t('estimationPage.highResGroupsValue', { used: m.highRes.groupsUsed, total: m.highRes.groupsTotal }) : null
                  ))}
                </TableRow>

                {highResSettings?.poolLowQuality && (
                  <TableRow hover>
                    {renderLabel(t('estimationPage.highResPooledLabel'), null)}
                    {models.map((m) => renderCell(
                      m.highRes?.groupsPooled > 0
                        ? t('estimationPage.highResPooledValue', { pooled: m.highRes.groupsPooled, total: m.highRes.groupsTotal })
                        : null
                    ))}
                  </TableRow>
                )}
              </>
            )}
            <TableRow hover>
              {renderLabel(
                <>
                  <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                    {t('estimationPage.explanationTitle')}
                  </Typography>
                </>,
                null
              )}
              {models.map((m) => (
                <TableCell
                  key={m.id}
                  align="left"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                    lineHeight: 1.6,
                    whiteSpace: 'pre-line',
                  }}
                >
                  {m.description || '-'}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ResultsPanel;
